import { Component, OnDestroy, OnInit } from '@angular/core';
import { SnackBarService } from 'app/services/snackbar.service';
import {
  TransactionProfileExportClient,
  TransactionProfileExportService,
  TransactionProfileExportStatus
} from './transaction-profile-export.service';

@Component({
  selector: 'app-transaction-profile-export',
  templateUrl: './transaction-profile-export.component.html',
  styleUrls: ['./transaction-profile-export.component.css'],
  providers: [TransactionProfileExportService]
})
export class TransactionProfileExportComponent implements OnInit, OnDestroy {

  clients: TransactionProfileExportClient[] = [];
  clientOptions: { label: string; value: number }[] = [];
  selectedClientOptions: { label: string; value: number }[] = [];
  exportInProgress = false;
  exportStatus: TransactionProfileExportStatus;
  exportProgress = 0;
  loadingClients = false;

  private pollInterval: any;

  constructor(
    private exportService: TransactionProfileExportService,
    private snackBarService: SnackBarService
  ) {}

  ngOnInit() {
    this.loadClients();
    this.checkAndResumeExportStatus();
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  loadClients() {
    this.loadingClients = true;
    this.exportService.getClientsWithProfiles().subscribe(
      clients => {
        this.clients = clients || [];
        this.clientOptions = this.clients.map(client => ({
          label: client.clientName + ' (' + client.ruleCount + ' rules, ' + client.agreementCount + ' agreements)',
          value: client.clientId
        }));
        this.loadingClients = false;
      },
      () => {
        this.loadingClients = false;
        this.snackBarService.showSimpleMsj('Failed to load clients with transaction profiles');
      }
    );
  }

  startExport() {
    if (this.exportInProgress) {
      return;
    }

    this.exportInProgress = true;
    this.exportStatus = null;
    this.exportProgress = 0;
    this.stopPolling();

    const clientIds = this.selectedClientOptions.length > 0
      ? this.selectedClientOptions.map(option => option.value)
      : undefined;
    this.exportService.startExport(clientIds).subscribe(
      response => {
        if (response && (response.status === 'started' || response.status === 'already_running')) {
          this.pollExportStatus();
        } else {
          this.exportInProgress = false;
          this.snackBarService.showSimpleMsj(response && response.message ? response.message : 'Failed to start export');
        }
      },
      err => {
        this.exportInProgress = false;
        this.snackBarService.showSimpleMsj('Failed to start export: ' + (err.message || 'Unknown error'));
      }
    );
  }

  downloadExport() {
    this.exportService.downloadExport().subscribe(
      blob => {
        const fileName = (this.exportStatus && this.exportStatus.fileName)
          || 'transaction_profiles_export.zip';
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
      },
      err => {
        this.snackBarService.showSimpleMsj('Failed to download export: ' + (err.message || 'Unknown error'));
      }
    );
  }

  isExportCompleted(): boolean {
    return this.exportStatus && this.exportStatus.status === 'completed';
  }

  isExportFailed(): boolean {
    return this.exportStatus && this.exportStatus.status === 'failed';
  }

  getElapsedTimeLabel(): string {
    if (!this.exportStatus || !this.exportStatus.startTime) {
      return '';
    }
    const end = this.exportStatus.endTime || Date.now();
    const seconds = Math.floor((end - this.exportStatus.startTime) / 1000);
    if (seconds < 60) {
      return seconds + 's';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return minutes + 'm ' + remainingSeconds + 's';
  }

  private checkAndResumeExportStatus() {
    this.exportService.getExportStatus().subscribe(
      status => {
        if (!status) {
          return;
        }
        this.exportStatus = status;
        if (status.progressPercentage !== undefined) {
          this.exportProgress = Math.round(status.progressPercentage);
        }
        if (status.status === 'running') {
          this.exportInProgress = true;
          this.pollExportStatus();
        } else if (status.status === 'completed' || status.status === 'failed') {
          this.exportInProgress = false;
        }
      },
      () => {}
    );
  }

  private pollExportStatus() {
    this.stopPolling();
    this.pollInterval = setInterval(() => {
      this.exportService.getExportStatus().subscribe(
        status => {
          if (!status) {
            return;
          }
          this.exportStatus = status;
          if (status.progressPercentage !== undefined) {
            this.exportProgress = Math.round(status.progressPercentage);
          }

          if (status.status === 'completed' || status.status === 'failed') {
            this.stopPolling();
            this.exportInProgress = false;
            if (status.status === 'completed') {
              this.snackBarService.showSimpleMsj(status.message || 'Export completed successfully');
            } else {
              this.snackBarService.showSimpleMsj(status.error || status.message || 'Export failed');
            }
          }
        },
        () => {}
      );
    }, 3000);
  }

  private stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/transaction-profile-export/transaction-profile-export.component.ts